import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const UsersOverviewOneProvinces = () => {
    const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
    const [donutChartSeries, setDonutChartSeries] = useState([]);
    const [donutChartOptions, setDonutChartOptions] = useState({
        colors: ["#4CAF50", "#2196F3", "#ff5e00ff", "#9C27B0", "#ffe607ff", "#F44336", "#3F51B5"],
        labels: [],
        chart: {
            type: "donut",
            height: 270,
            events: {
                dataPointMouseEnter: function(event, chartContext, config) {
                    // This will be handled by the custom tooltip
                }
            }
        },
        legend: {
            show: false,
        },
        stroke: {
            width: 0,
        },
        dataLabels: {
            enabled: false,
        },
        tooltip: {
            enabled: true,
            custom: function({ series, seriesIndex, w }) {
                // Custom tooltip with the desired format
                return `<div class="custom-tooltip">
                    <span>${series[seriesIndex]} Plants planted</span>
                </div>`;
            }
        },
        responsive: [
            {
                breakpoint: 480,
                options: {
                    chart: {
                        width: 200,
                    },
                    legend: {
                        position: "bottom",
                    },
                },
            },
        ],
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(`${API_BASE_URL}/api/plantation/pafs/province-wise-plantation`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const result = await response.json();
                    if (result.success) {
                        // Define the desired order of provinces
                        const desiredOrder = [
                            'Punjab',
                            'Sindh',
                            'Balochistan',
                            'Azad Jammu & Kashmir',
                            'Islamabad Capital Territory',
                            'Gilgit-Baltistan',
                            'Khyber Pakhtunkhwa'
                        ];

                        // Create a mapping of provinces to their respective colors and counts
                        const provinceInfo = {
                            'Punjab': { color: "#4CAF50", count: 0 }, // Green
                            'Sindh': { color: "#2196F3", count: 0 }, // Blue
                            'Balochistan': { color: "#ff5e00ff", count: 0 }, // Orange
                            'Azad Jammu & Kashmir': { color: "#9C27B0", count: 0 }, // Purple
                            'Islamabad Capital Territory': { color: "#ffe607ff", count: 0 }, // Amber
                            'Gilgit-Baltistan': { color: "#F44336", count: 0 }, // Red
                            'Khyber Pakhtunkhwa': { color: "#3F51B5", count: 0 } // Indigo
                        };

                        // Update counts from the API response
                        result.data.forEach(item => {
                            if (provinceInfo[item.province]) {
                                provinceInfo[item.province].count = item.count;
                            }
                        });

                        // Prepare series and labels in the desired order
                        const series = [];
                        const labels = [];
                        const colors = [];

                        desiredOrder.forEach(province => {
                            if (provinceInfo[province]) {
                                series.push(provinceInfo[province].count);
                                labels.push(province);
                                colors.push(provinceInfo[province].color);
                            }
                        });

                        // Set the donut chart series and options
                        setDonutChartSeries(series);
                        setDonutChartOptions(prevOptions => ({
                            ...prevOptions,
                            labels: labels,
                            colors: colors,
                        }));
                    }
                } else {
                    console.error('Failed to fetch data:', response.statusText);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [API_BASE_URL]);

    // Add CSS for custom tooltip
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .custom-tooltip {
                background: #fff;
                padding: 8px 12px;
                border-radius: 4px;
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                font-weight: bold;
                border: 1px solid #e0e0e0;
            }
            .custom-tooltip span {
                color: #333;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    return (
        <div className="col-xxl-4 col-xl-4" style={{ marginTop: '16px' }}>
            <div className="card h-100 radius-8 border-0 overflow-hidden">
                <div className="card-body p-24">
                    <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between">
                        <h6 className="mb-2 fw-bold text-lg">Provincial Stats</h6>
                    </div>
                    {loading ? (
                        <Skeleton height={264} />
                    ) : (
                        <ReactApexChart 
                            options={donutChartOptions} 
                            series={donutChartSeries} 
                            type="donut" 
                            height={264} 
                        />
                    )}
                    <ul className="d-flex flex-wrap align-items-center justify-content-between mt-3 gap-3">
                        {loading ? (
                            Array.from({ length: 7 }).map((_, index) => (
                                <li key={index} className="d-flex align-items-center gap-2">
                                    <Skeleton circle width={12} height={12} />
                                    <Skeleton width={80} height={12} />
                                </li>
                            ))
                        ) : (
                            donutChartOptions.labels.map((label, index) => (
                                <li key={index} className="d-flex align-items-center gap-2">
                                    <span className={`w-12-px h-12-px radius-2`} style={{ backgroundColor: donutChartOptions.colors[index] }} />
                                    <span className="text-secondary-light text-sm fw-normal">
                                        <span className="text-primary-light fw-semibold">{label}</span>
                                    </span>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default UsersOverviewOneProvinces;